import React from 'react';
import { useQuery } from 'react-query';
import { useDispatch } from 'react-redux';
import { queryKeys } from './queryKeys';
import { CATEGORY_ICON_MAP, CategoryName } from '../Workflow/Workflow.meta';
import { authFetch } from '../../CommonUtils/authFetch';
import { urlWithParams } from '../../CommonUtils/urlWithParams';
import { notifyPdfLoadedSection } from '../../PDFRenderer/PDFRendererActions';
import { PdfSectionType } from '../../PDFRenderer/PDFRendererTypes';

export interface GoalDetailDTO {
  valueType: 'Number' | 'Amount' | 'Percent' | 'Date' | 'String';
  value: number | string;
  title: string;
}

export interface GoalDTO {
  id: string;
  title: string;
  subtitle: string;
  status: string;
  statusLevel: 0 | 1 | 2;
  details: GoalDetailDTO[];
  isComplete: boolean;
  navigationData: {
    GoalId: string;
  } | null;
}

export interface CategoryResultDTO {
  id: CategoryName;
  priority: number;
  description: string;
  tip: string;
  fulfillment: number;
  goals: GoalDTO[];
}

interface FetcherParams {
  clientId: string;
}

export interface CategoryResult extends CategoryResultDTO {
  icon: React.FC;
}

export interface ResultsDTO {
  categories: CategoryResultDTO[];
  respondedOn: string;
}

export interface PulseCheckResults extends Exclude<ResultsDTO, 'categories'> {
  categories: CategoryResult[];
  respondedOn: string;
}

export const fetchResults = async ({ clientId }: FetcherParams): Promise<PulseCheckResults> => {
  const data = (await authFetch(
    urlWithParams('/Consumer/PulseCheckResult', { clientId }),
  )) as ResultsDTO;

  return {
    ...data,
    categories: data.categories
      .map(category => ({
        ...category,
        icon: CATEGORY_ICON_MAP[category.id],
      }))
      .sort(({ priority: priorityA }, { priority: priorityB }) => priorityA - priorityB),
  };
};

interface QueryParams {
  clientId: string;
}

export const useResultsQuery = ({ clientId }: QueryParams) => {
  const dispatch = useDispatch();

  return useQuery(
    queryKeys.results(clientId),

    () => fetchResults({ clientId }),

    {
      cacheTime: 0,

      onSuccess() {
        dispatch(notifyPdfLoadedSection(PdfSectionType.PulseCheck));
      },
    },
  );
};
